import { useRef, useState } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { useFeedbackItemCalls } from 'components/Common/ThumbsDown';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useParams } from 'react-router-dom';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';

const FeedbackOnItem = (props) => {
  const { contentResults } = useContentResults();
  const { contentPage, item, index, updateFeedback, feedback, keywordsFromLists, customClass, explanations } = props;
  const feedbackProperties = {
    contentPage,
    item,
    index,
    updateFeedback,
    feedback,
    keywordsFromLists,
    explanations,
  };
  const sectionName = createSectionName();
  const section = contentResults[sectionName];
  const { feedItemFlags } = section ?? {};

  const feedItem = feedItemFlags?.[index];
  const editFeedbackCondition = contentPage ? !!feedback?.ratingReason : !!feedItem?.ratingReason;

  const isMobile = useIsMobile(1175);
  const singleButtonCondition = isMobile || editFeedbackCondition || location.pathname.includes('/dashboard');

  const topicParam = getUrlParam('topic-id');
  const isTopicANumber = topicParam ? Number.isInteger(parseInt(topicParam)) : false;

  const params = useParams();
  const { inboxSection, dashboardSection } = useMainSectionParameter(params);

  return (
    <>
      {(isTopicANumber || inboxSection || dashboardSection) && (
        <div
          className={`flex-centered general-feedback-container ${
            singleButtonCondition
              ? `${contentPage ? '' : 'item-share-feedback-container-single-button'}`
              : 'justify-content-between'
          } ${
            contentPage
              ? 'pt-2 pb-3 border-bottom'
              : 'border-top rounded-bottom item-share-feedback-container item-size-text'
          } ${customClass ?? ''}`}
        >
          <div className={`d-flex align-items-lg-center`}>
            <p className='main-subtle-text mb-0 mr-1'>This update has been marked as irrelevant</p>
          </div>
          <div>
            {singleButtonCondition ? (
              <FeedbackSingleButton {...feedbackProperties} />
            ) : (
              <FeedbackButtons {...feedbackProperties} />
            )}
          </div>
        </div>
      )}
    </>
  );
};

const FeedbackSingleButton = (props) => {
  //AE: Keep properties in this way to know exactly what are needed for the component
  const { contentPage, item, index, updateFeedback, feedback, keywordsFromLists, explanations } = props;
  const feedbackProperties = {
    contentPage,
    item,
    index,
    updateFeedback,
    feedback,
    keywordsFromLists,
    explanations,
  };
  const [isOpen, setIsOpen] = useState(false);
  const actionsElement = useRef(null);

  return (
    <div className='position-relative' ref={actionsElement}>
      <button
        className={`indented-button general-button circle-button item-actions-button item-actions-button-feedback ${
          isOpen ? 'active' : ''
        }`}
        onClick={(e) => {
          preventLink(e);
          setIsOpen(!isOpen);
        }}
      >
        Feedback actions
      </button>
      {isOpen && (
        <div className='box box-shadow py-2 item-actions-box item-actions-box-feedback'>
          <FeedbackButtons {...feedbackProperties} isFromButton={true} />
        </div>
      )}
    </div>
  );
};

const FeedbackButtons = (props) => {
  const { contentResults, setContentResults, setFeedState } = useContentResults();

  const {
    item,
    index,
    contentPage,
    updateFeedback,
    feedback,
    keywordsFromLists,
    explanations: explanationsProps,
  } = props;
  const [loadingStates, setLoadingStates] = useState({
    view: false,
    undo: false,
  });

  const { removeFeedback } = useFeedbackItemCalls();

  const sectionName = createSectionName();
  const section = contentResults[sectionName];
  const { feedItemFlags } = section ?? {};

  const feedItem = feedItemFlags?.[index];
  const explanations = explanationsProps ?? feedItem?.explanations ?? [];
  const editFeedbackCondition = contentPage ? !!feedback?.ratingReason : !!feedItem?.ratingReason;

  const { generalApiCall } = useGeneralApiCall();
  const { dashboardSection, priorityUpdatesSection } = useMainSectionParameter();

  const getItemFeedback = async () => {
    const { objectID, parentObjectID } = item;
    const pathname = `/api/feed/item-feedback?objectId=${parentObjectID ?? objectID}`;
    const method = 'get';
    return await generalApiCall({
      pathname,
      method,
      needsAuthentication: true,
    });
  };

  const removeFeedbackReason = async () => {
    const { objectID, parentObjectID } = item;
    setLoadingStates({ ...loadingStates, undo: true });
    let results = await removeFeedback({
      objectId: parentObjectID ?? objectID,
    });
    if (results?.status === 200) {
      let newSection = { ...section };
      if (index !== undefined && index >= 0 && !dashboardSection && !priorityUpdatesSection) {
        newSection.feedItemFlags[index] = {
          ...newSection.feedItemFlags[index],
          ratingReason: null,
          ratingScored: null,
        };
      }
      if (contentPage || priorityUpdatesSection || dashboardSection) {
        if (updateFeedback) {
          //AE: CASE FOR INFLUENCE ITEMS
          updateFeedback({ feedback: null });
        } else {
          let parameter = sectionName === 'LibraryMaterial' ? 'content' : 'new';
          newSection[parameter] = {
            ...newSection[parameter],
            feedback: null,
          };
        }
      }

      setContentResults({
        ...contentResults,
        [sectionName]: newSection,
      });
    }
  };

  return (
    <>
      <button
        className={`general-button nowrap-item outline-active-button px-3 py-1 mr-3`}
        onClick={(e) => {
          removeFeedbackReason();
          e.stopPropagation();
          e.preventDefault();
        }}
        disabled={loadingStates?.undo}
      >
        {loadingStates?.undo ? <i className='fas fa-spinner fa-spin d-inline-block-centered mr-2' /> : ''} Undo
      </button>
      <button
        className={`general-button nowrap-item action-button px-3 py-1 border-general border-light-blue`}
        onClick={async (e) => {
          e.stopPropagation();
          e.preventDefault();
          setLoadingStates({ ...loadingStates, view: true });
          const feedback = await getItemFeedback();
          if (feedback) {
            const searchTermMatches = contentPage
              ? keywordsFromLists
              : explanations?.reduce((accumulator, currentValue) => {
                  let terms = currentValue?.searchTermMatches;
                  terms?.forEach((item) => {
                    if (!accumulator.includes(item?.searchTerm)) {
                      accumulator.push(item?.searchTerm);
                    }
                  });
                  return accumulator;
                }, []);
            setFeedState('feedbackItemPopup', {
              isHandPicked: feedItem?.isHandPicked,
              ratingScored: 'Negative',
              isOpen: true,
              item,
              index,
              keywords: searchTermMatches,
              feedback,
              contentPage,
              updateFeedback,
            });
            setLoadingStates({ ...loadingStates, view: false });
          }
        }}
      >
        {loadingStates?.view ? <i className='fas fa-spinner fa-spin d-inline-block mr-2' /> : ''}
        {editFeedbackCondition ? 'Edit' : 'Share'} feedback
      </button>
    </>
  );
};

export default FeedbackOnItem;
